import React, { useState, useEffect, useRef } from "react";
import Modal from "react-bootstrap/Modal";
import LeaveConfirmModal from "./LeaveConfirmModal";
import { QRCodeIcon, CheckIcon, CloseIcon, ClockIcon, ClockWaitIcon } from "../../../image/icons";

const STATUS_ICON = {
  "00": QRCodeIcon,
  success: CheckIcon,
  rejected: CloseIcon,
  errorQr: CloseIcon,
  invalidQR: CloseIcon,
  expired: ClockIcon,
  limit_reached: ClockWaitIcon,
  error: CloseIcon
};

const CONTENT = {
  "00": { title: "Authentication Request Sent", subtitle: "Waiting for customer approval in their Tonik App." },
  success: { title: "Authentication Successful", subtitle: "The customer has verified their identity. You may now proceed with the loan application." },
  rejected: { title: "Authentication Rejected", subtitle: "The customer declined the authentication request in their Tonik App." },
  errorQr: { title: "An error has occurred", subtitle: (msg) => msg || "An error occurred while processing this QR code." },
  expired: { title: "Authentication Request Expired", subtitle: "No response was received within 15 minutes. The customer will need to generate or use their QR again." },
  limit_reached: { 
    title: "Authentication Limit Reached", 
    subtitle: (max, timeoutSec) => {
      const mins = timeoutSec ? Math.ceil(timeoutSec / 60) : 15;
      return `You've reached the authentication attempt limit for this QR code (${max} of ${max}). You can retry this QR code after the ${mins}-minute waiting period. You may continue scanning other customers' QR codes in the meantime.`;
    }
  },
  error: { title: "An error has occurred", subtitle: (msg) => msg },
  invalidQR: { title: "An error has occurred", subtitle: "QR code is invalid" }
};

export const Authentication = (logic) => {
  // Unify unpacking so root or nested properties both resolve cleanly
  let rawData = logic.data || {};
  let nestedData = rawData.data || {};
  let data = {
    ...nestedData,
    ...rawData,
    attempt: rawData.attempt || nestedData.attempt || nestedData.attemptCurrent ,
    "re-attempt-threshold": rawData["re-attempt-threshold"] || nestedData["re-attempt-threshold"] ,
  };
  
  const [reAttemptDisabled, setReAttemptDisabled] = useState(true);
  const [timeoutSecondsLeft, setTimeoutSecondsLeft] = useState(data["timeout-time"] || 120);
  const [cooldownSecondsLeft, setCooldownSecondsLeft] = useState(data["limit-reached-timeout"] || 0);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  const handleAttemptClose = () => {
    if (data.status === "success") {
      logic.onContinue && logic.onContinue();
    } else {
      setShowLeaveConfirm(true);
    }
  };

  const handleLeaveCancel = () => {
    setShowLeaveConfirm(false);
  };

  const handleLeaveConfirmed = () => {
    setShowLeaveConfirm(false);
    if (logic.onClose) {
      logic.onClose();
    } else if (logic.onScanNew) {
      logic.onScanNew();
    }
  };

  const timeoutRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const cooldownRef = useRef(null);
  // Keep a live ref to the latest callback so the interval never calls a stale closure.
  const onPollStatusRef = useRef(logic.onPollStatus);
  onPollStatusRef.current = logic.onPollStatus;

  useEffect(() => {
    if (data.status !== "00") return;

    timeoutRef.current = setInterval(() => {
      setTimeoutSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timeoutRef.current);
          clearInterval(pollIntervalRef.current);
          setReAttemptDisabled(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    const pollingIntervalMs = (data["polling-time"] || 10) * 1000;
    pollIntervalRef.current = setInterval(() => {
      if (onPollStatusRef.current) {
        onPollStatusRef.current();
      }
    }, pollingIntervalMs);

    return () => {
      clearInterval(timeoutRef.current);
      clearInterval(pollIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.status]);

  // Live cooldown countdown once the attempt limit is reached.
  useEffect(() => {
    if (data.status !== "limit_reached" || !data["limit-reached-timeout"]) return;

    cooldownRef.current = setInterval(() => {
      setCooldownSecondsLeft((prev) => {
        if (prev <= 1) {
          clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(cooldownRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.status]);


  const formatCooldown = (totalSeconds) => {
    const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
    const ss = String(totalSeconds % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const IconComponent = STATUS_ICON[data.status] || (() => <></>);
  const content = CONTENT[data.status];
  const title = content?.title ?? "";
  
  // limit_reached's subtitle expects the attempt threshold; everything else expects the message.
  const subtitleArg = data.status === "limit_reached"
    ? data["re-attempt-threshold"]
    : (nestedData.message || rawData.message || data.message);
  const subtitle = typeof content?.subtitle === "function"
    ? content.subtitle(subtitleArg, data["limit-reached-timeout"])
    : content?.subtitle ?? "";

  const renderBadge = () => {
    if (data.status === "00") {
      return (
        <p className="auth-badge--00">
          <span style={{ marginRight: "6px" }}>●</span>
          Attempt {data.attempt} of {data["re-attempt-threshold"]}
        </p>
      );
    }
    if (data.status === "rejected") {
      return (
        <p className="auth-badge--rejected">
          Attempt {data.attempt} of {data["re-attempt-threshold"]} used
        </p>
      );
    }
    if (data.status === "success") {
      return (
        <p className="auth-badge--success">
          <span style={{ marginRight: "6px" }}>●</span>
          Customer verified
        </p>
      );
    }
    return null;
  };

  const renderActions = () => {
    if (data.status === "00") {
      return (
        <>
        <div className="auth-countdown-box">
            <p className="auth-countdown-label">RETRY AVAILABLE IN</p>
            <p className="auth-countdown-timer">{formatCooldown(timeoutSecondsLeft)}</p>
          </div>

        <button 
          className={`auth-btn-primary ${reAttemptDisabled ? "auth-btn-primary--disabled" : ""}`} 
          disabled={reAttemptDisabled}
          onClick={logic.onReAttempt ?? (() => {})}
        >
          Re-attempt authentication
        </button>
        </>
      );
    }
    if (data.status === "success") {
      return (
        <button className="auth-btn-primary" onClick={logic.onContinue ?? (() => {})}>
          Continue →
        </button>
      );
    }
    if (data.status === "rejected" || data.status === "expired") {
      return (
        <>
          <button className="auth-btn-primary" onClick={logic.onScanNew ?? (() => {})}>
            Scan a new QR →
          </button>
          <button className="auth-btn-ghost" onClick={logic.onScanNew ?? (() => {})}>
            Restart
          </button>
        </>
      );
    }
    if (data.status === "errorQr" || data.status === "error") {
      return (
        <button className="auth-btn-primary" onClick={logic.onScanNew ?? (() => {})}>
          {logic.errorActionLabel || "Rescan"}
        </button>
      );
    }
    if (data.status === "invalidQR") {
      return (
        <button className="auth-btn-primary" onClick={logic.onScanNew ?? (() => {})}>
          {logic.errorActionLabel || "Rescan"}
        </button>
      );
    }
    if (data.status === "limit_reached") {
      const cooldownDone = cooldownSecondsLeft === 0;
      return (
        <>
          <div className="auth-countdown-box">
            <p className="auth-countdown-label">You can retry this QR code in</p>
            <p className="auth-countdown-timer">{formatCooldown(cooldownSecondsLeft)}</p>
          </div>
          <button
            className={`auth-btn-primary ${!cooldownDone ? "auth-btn-primary--disabled" : ""}`}
            disabled={!cooldownDone}
            onClick={logic.onScanNew ?? (() => {})}
          >
            Scan a new QR →
          </button>
        </>
      );
    }
  };

  return (
    <>
      <Modal
        show={logic.show ?? true}
        onHide={handleAttemptClose}
        animation={false}
        centered
        dialogClassName="authentication-modal"
        backdrop="static"
        className="vip-authentication-modal"
      >
        <Modal.Header closeButton className="cc-header" />
        <Modal.Body className="auth-body">
          <div className="auth-icon-wrap">
            <IconComponent />
          </div>
          <h2 className="auth-title">{title}</h2>
          <p className="auth-subtitle">{subtitle}</p>
          {renderBadge()}
          <div className="auth-actions">{renderActions()}</div>
        </Modal.Body>
      </Modal>

      <LeaveConfirmModal
        show={showLeaveConfirm}
        onCancel={handleLeaveCancel}
        onConfirm={handleLeaveConfirmed}
      />
    </>
  );
};

export default Authentication;